import { Helmet } from 'react-helmet';
import { Box } from '@mui/material';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { db } from '../../firebase/config';
import styles from './SignageScheduler.module.css';
import facilities from '../../config/facilities.json';
import { getUTCDate, getNewYorkTimeZoneDate } from '../../utils/dayjsUs';

const schedulerSetData = [
  {
    sectionName: 'Day Nurse',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Night Nurse',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Day STNA',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Night STNA',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
];
function SignageScheduler() {
  /** URL params
   * facility: facility id (admirals-pointe) REQUIRED
   */

  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facility');

  const [facility, setFacility] = useState({});
  const [schedulerData, setSchedulerData] = useState(schedulerSetData);
  const [today, setToday] = useState(getNewYorkTimeZoneDate());

  useEffect(() => {
    let currentDate = getNewYorkTimeZoneDate().format('YYYY-MM-DD');
    const interval = setInterval(
      () => {
        const newDate = getNewYorkTimeZoneDate().format('YYYY-MM-DD');
        if (newDate !== currentDate) {
          window.location.reload(); // Refresh page at midnight
        }
        currentDate = newDate; // Update current date
        setToday(getNewYorkTimeZoneDate());
      },
      60 * 60 * 1000, // 1 hours
    );

    return () => clearInterval(interval);
  }, []);

  const tableRef = useRef(null);
  const fetchSchedulerData = () => {
    const q = query(
      collection(db, 'scheduler'),
      where('date', '==', today.format('MM/DD/YYYY')),
      where('facilityId', '==', facilityId),
    );

    const unsubscribeScheduler = onSnapshot(q, (querySnapshot) => {
      let schedulerData = [];
      querySnapshot.forEach((doc) => {
        schedulerData.push({ ...doc.data(), id: doc.id });
      });

      setSchedulerData(
        schedulerData.length > 0 ? schedulerData[0].schedulerContent : [],
      );
    });

    return unsubscribeScheduler;
  };
  useEffect(() => {
    const unsubscribe = fetchSchedulerData();

    // Clean up on component unmount or when dependencies change
    return () => {
      unsubscribe();
    };
  }, [facilityId, today]);

  useEffect(() => {
    const found = facilities.find((x) => x.id === facilityId);
    if (!found) {
      window.alert('Invalid facility!');
      return;
    }
    setFacility(found);
  }, [facilityId]);

  useEffect(() => {
    const calculateFontSize = () => {
      const table = tableRef.current;
      if (!table) return;

      const baseFontSize = 20;
      const maxTableHeightVh = 70;
      const maxTableHeightPx = (maxTableHeightVh * window.innerHeight) / 100;
      const minFontSize = 6;
      const decrement = 0.25;
      const delay = 50;

      let fontSize = baseFontSize;

      // Initial delay to ensure rendering is complete
      setTimeout(() => {
        const adjustFontSize = () => {
          table.style.fontSize = `${fontSize}px`;
          const tableHeight = table.offsetHeight;

          if (tableHeight > maxTableHeightPx && fontSize > minFontSize) {
            fontSize -= decrement;
            setTimeout(adjustFontSize, delay);
          } else {
            table.style.fontSize = `${Math.min(Math.max(fontSize, minFontSize), baseFontSize)}px`;
          }
        };

        adjustFontSize();
      }, 5000);
    };

    calculateFontSize();
  }, []);

  return (
    <div
      style={{
        '--facilityDarkColor': facility.darkColor,
        '--facilityLightColor': facility.lightColor,
        '--facilitytextColor': facility.textColor,
      }}
      className={styles.schedulerContainer}
    >
      <Helmet>
        <style>{`
        body {
          scrollbar-width: none; /* For Firefox */
          -ms-overflow-style: none;  /* For Internet Explorer and Edge */
        }
        ::-webkit-scrollbar {
          display: none; /* For Chrome, Safari and Opera */
        }
      `}</style>
      </Helmet>
      <div className={styles.schedulerHeader}>
        <h2 className={styles.schedulerHeaderTitle}>
          Daily Nursing Staffing Sheet
        </h2>
        <div className={styles.facilityLogoContainer}>
          {facility?.logo && (
            <img
              // eslint-disable-next-line no-undef
              src={require(`../../assets/facility-logos/${facility.logo}`)}
              className={styles.facilityLogo}
              alt="Facility logo"
            />
          )}
        </div>

        <div className={styles.schedulerDateDayContainer}>
          <div className={styles.date}>Date: {today.format('MM/DD/YYYY')}</div>
          <div className={styles.day}>Day: {today.format('dddd')}</div>
        </div>
      </div>

      <Box className={styles.schedulerTable} ref={tableRef}>
        {schedulerData.map((section, sectionIndex) => (
          <div className={styles.schedulerTableSegment} key={sectionIndex}>
            <div className={styles.segmentHeader}>{section.sectionName}</div>

            {section.sectionData.map((row, rowIndex) => (
              <div className={styles.segmentRow} key={rowIndex}>
                <div className={styles.rowLocation}>{row.roomNumber}</div>
                <div className={styles.rowStaff}>{row.staffName}</div>
              </div>
            ))}
          </div>
        ))}
      </Box>
    </div>
  );
}

SignageScheduler.propTypes = {};

export default SignageScheduler;
